import React, { useState } from 'react';
import { compose } from 'redux';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';

import routeConfiguration from '../../routeConfiguration';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import {
  loadData,
  addCartToUser,
  deleteCartListingAndRemoveLocally,
  updateCartListingAndRefetchTimeSlot,
} from './CartPage.slice';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  IconSpinner,
  Order,
  ServiceFeeSummary,
} from '../../components';
import Summary from './Summary/Summary';
import { TopbarContainer } from '..';
import { setOrderedProducts } from '../../components/TransactionPanel/setOrderedProducts';
import QuantitySelector from './QuantitySelector/QuantitySelector';
import ServiceFeeItem from '../../components/ServiceFeeItem/ServiceFeeItem';

import css from './CartPage.css';

const subtractOneDay = dateString => {
  if (!dateString) return dateString;
  const date = new Date(dateString);
  date.setDate(date.getDate() - 1);
  return date.toISOString().split('T')[0];
};

const CartPageComponent = ({
  intl,
  cart = [],
  fetchInProgress = false,
  fetchCartError = null,
  deleteFromCart,
  history,
  callSetInitialValues,
  onInitializeCardPaymentData,
  onAddCartToUser,
  fetchServiceFeesInProgress,
  pendingServiceFeeTransaction,
  fetchServiceFeesError,
  cartData,
  updateCartListingAmount,
  updateCartListingInProgress,
  updatingCartListingId,
  deleteFromCartInProgress,
  deletingListingId,
  fetchCartDataInProgress
}) => {
  const { timeSlots } = cartData;
  const [debouncingListingId, setDebouncingListingId] = useState(null);

  const sortedCart = [...cart]
    .sort((cartA, cartB) => cartA.id.uuid.localeCompare(cartB.id.uuid))
    .map(order => ({
      ...order,
      cartListings: [...(order.cartListings || [])].sort((listingA, listingB) =>
        listingA.id.uuid.localeCompare(listingB.id.uuid)
      ),
    }));

  const serviceFeesWithListings = pendingServiceFeeTransaction.map(transaction => {
    const relatedTransaction = transaction.relatedTransactions.find(
      item =>
        !item.attributes.serviceFee &&
        item.attributes.processState !== 'pending-payment' &&
        item.attributes.processState !== 'payment-expired' &&
        moment(item.attributes.start).isSame(moment(transaction.attributes.start)) &&
        moment(item.attributes.end).isSame(moment(transaction.attributes.end))
    );

    const {
      lineItems: relatedLineItems,
      transitions: relatedTransitions,
    } = relatedTransaction.attributes;

    const listings =
      relatedLineItems?.length &&
      relatedTransitions.length &&
      setOrderedProducts(relatedLineItems, relatedTransitions);

    const currentListings = relatedTransaction.listings.filter(item => {
      const actualListing = listings.find(el => el.listingId === item.id.uuid);
      return actualListing.seats > 0;
    });

    return {
      ...transaction,
      cartListings: currentListings.map(listing => ({
        attributes: {
          amount: relatedTransaction.attributes.lineItems.findLast(
            item => item?.listingId === listing.id.uuid
          )?.seats,
        },
        id: { uuid: listing.id.uuid },
        listing,
      })),
    };
  });

  const user = useSelector(state => state?.user?.currentUser);
  const currentUserProviders = useSelector(state => state.state?.user?.currentUserProviders);
  const isAddToUserAvailable = !!user?.attributes?.hasProvider;

  const errorMessage = fetchCartError ? (
    <p className={css.error}>{fetchCartError.message}</p>
  ) : null;

  const noResultsMessage =
    !fetchInProgress &&
    !cart.length &&
    !fetchCartError &&
    !fetchServiceFeesInProgress &&
    !pendingServiceFeeTransaction.length &&
    !fetchServiceFeesError ? (
      <p className={css.noResultText}>
        <FormattedMessage id="Order.noOrders" />
      </p>
    ) : null;

  const handleSubmit = (values, isServiceFee = false) => {
    const { bookingDates, ...bookingData } = values;

    const initialValues = {
      bookingDates: {
        bookingStart: values.start,
        bookingEnd: values.end,
      },
      confirmPaymentError: null,
      listings: values.cartListings,
      provider: values.provider,
      totalNumberOfOrders: values.totalNumberOfOrders,
      internalUse: values.internalUse,
    };

    const routes = routeConfiguration();
    // Customize checkout page state with current listing and selected bookingDates
    const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);
    callSetInitialValues(setInitialValues, initialValues);

    // Clear previous Stripe errors from store if there is any
    onInitializeCardPaymentData();
    const pathParams = isServiceFee ? { cartId: 'serviceFee' } : { cartId: values.cart };
    const searchParamsMaybe = isServiceFee ? { serviceFeeId: values.cart.uuid } : {};

    // Redirect to CheckoutPage
    history.push(
      createResourceLocatorString('CheckoutPage', routes, pathParams, searchParamsMaybe)
    );
  };

  return (
    <Page
      className={css.root}
      title={intl.formatMessage({ id: 'CartPage.title' })}
      scrollingDisabled={false}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="CartPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain className={css.main}>
          <div className={css.cartContainer}>
            {errorMessage}
            {noResultsMessage}
            { fetchCartDataInProgress ? (
              <div className={css.listItemsLoading}>
                <IconSpinner />
              </div>
            ) : (
              <>
                {serviceFeesWithListings.map((item, index) => {
                  const { cartListings, attributes, provider, id, relatedTransactions } = item;
                  const serviceFeeFor = relatedTransactions.filter(
                    tx =>
                      !tx.attributes.serviceFee &&
                      tx.attributes.processState !== 'pending-payment' &&
                      tx.attributes.processState !== 'payment-expired'
                  );

                  return (
                    <ServiceFeeItem
                      key={id.uuid}
                      orders={cartListings}
                      orderNumber={index + 1}
                      totalNumberOfOrders={serviceFeesWithListings.length}
                      intl={intl}
                      provider={provider}
                      className={css.orderContainer}
                      serviceFee={attributes.serviceFee}
                      payinTotal={attributes.payinTotal}
                      serviceFeeFor={serviceFeeFor}
                    >
                      <ServiceFeeSummary
                        intl={intl}
                        transaction={item}
                        isPayment={true}
                        rootClassName={css.summaryCard}
                        contextText={intl.formatMessage({ id: 'CartPage.serviceFee' })}
                      />
                    </ServiceFeeItem>
                  );
                })}
                {sortedCart.map(({ cartListings, attributes, provider, id }, index) => {
                  const isAnyQuantityExceeded = cartListings.some(order => {
                    const initialValue = order.attributes?.amount || 1;
                    const cartListingTimeSlots =
                      !!timeSlots?.length &&
                      timeSlots.find(
                        item =>
                          item.cartListingId === order.id.uuid &&
                          item.listingId.uuid === order?.listing?.id?.uuid &&
                          item.start === order.attributes.start &&
                          item.end === subtractOneDay(order.attributes.end)
                      );
                    const slots = cartListingTimeSlots?.slots || [];
                    if (!slots.length) return false;

                    const maxAvailableSlots = Math.min(...slots.map(slot => slot.attributes.seats));
                    return maxAvailableSlots < initialValue;
                  });

                  const isOwnerOfThisProvider = currentUserProviders?.some(
                    p => p?.id?.uuid === provider?.id?.uuid && p?.myRole === 'owner'
                  );

                  const canBookInternally =
                    user?.attributes?.isAdmin ||
                    user?.attributes?.adminForCountries?.includes(
                      provider?.attributes?.countryId
                    ) ||
                    isOwnerOfThisProvider;

                  const isOrderDebouncing = cartListings.some(
                    order => order.id.uuid === debouncingListingId
                  );

                  const isOrderUpdating =
                    updateCartListingInProgress &&
                    cartListings.some(order => order.id.uuid === updatingCartListingId);

                  const isOrderDeleting =
                    deleteFromCartInProgress &&
                    cartListings.some(order => order.id.uuid === deletingListingId);

                  const isOrderRequestInFlight = isOrderUpdating || isOrderDeleting;

                  const isThisOrderPending = isOrderDebouncing || isOrderRequestInFlight;

                  return (
                    <Order
                      key={id.uuid}
                      orders={cartListings}
                      orderNumber={index + 1}
                      totalNumberOfOrders={cart.length}
                      intl={intl}
                      provider={provider}
                      deleteFromCart={deleteFromCart}
                      productCanBeRemoved={true}
                      isDisabledDeleteButton={isThisOrderPending}
                      className={css.orderContainer}
                      renderQuantitySelector={order => {
                        const cartListingTimeSlots =
                          !!timeSlots?.length &&
                          timeSlots.find(
                            item =>
                              item.cartListingId === order.id.uuid &&
                              item.listingId.uuid === order?.listing?.id?.uuid &&
                              item.start === order.attributes.start &&
                              item.end === subtractOneDay(order.attributes.end)
                          );
                        return (
                          <QuantitySelector
                            initialValue={order.attributes?.amount || 1}
                            bookingDates={{
                              startDate: attributes.start,
                              endDate: attributes.end,
                            }}
                            timeSlots={cartListingTimeSlots?.slots || []}
                            disabled={isOrderRequestInFlight}
                            onPendingStateChange={isPending => {
                              setDebouncingListingId(isPending ? order.id.uuid : null);
                            }}
                            onQuantityChange={newQuantity => {
                              updateCartListingAmount({
                                cartListingId: order.id.uuid,
                                amount: newQuantity,
                                listingId: order?.listing?.id?.uuid,
                                start: order.attributes.start,
                                end: order.attributes.end,
                              });
                            }}
                          />
                        );
                      }}
                      productDetailsClassName={css.productDeatils}
                    >
                      <Summary
                        start={attributes.start}
                        end={attributes.end}
                        payinTotal={attributes.payinTotal}
                        lineItems={attributes.lineItems}
                        cartListings={cartListings}
                        intl={intl}
                        totalNumberOfOrders={cart.length}
                        handleSubmit={handleSubmit}
                        provider={provider}
                        id={id.uuid}
                        onAddCartToUser={onAddCartToUser}
                        isAddToUserAvailable={isAddToUserAvailable}
                        isBookDisabled={isAnyQuantityExceeded || isThisOrderPending}
                        canBookInternally={canBookInternally}
                        inProgress={isThisOrderPending }
                      />
                    </Order>
                  );
                })}
              </>
            )}{' '}
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const {
    fetchInProgress,
    fetchCartError,
    cart,
    deleteFromCartInProgress,
    deleteFromCartSuccess,
    pendingServiceFeeTransaction,
    fetchServiceFeesInProgress,
    fetchServiceFeesError,
    cartData,
    updateCartListingSuccess,
    updateCartListingInProgress,
    updatingCartListingId,
    deletingListingId,
    fetchCartSuccess,
    fetchCartDataInProgress
  } = state.CartPage;

  return {
    fetchInProgress,
    fetchCartError,
    scrollingDisabled: isScrollingDisabled(state),
    cart: getMarketplaceEntities(state, cart),
    deleteFromCartInProgress,
    deleteFromCartSuccess,
    fetchServiceFeesInProgress,
    pendingServiceFeeTransaction,
    fetchServiceFeesError,
    cartData,
    updateCartListingSuccess,
    updateCartListingInProgress,
    updatingCartListingId,
    deletingListingId,
    fetchCartSuccess,
    fetchCartDataInProgress
  };
};

const mapDispatchToProps = dispatch => ({
  deleteFromCart: listingId => dispatch(deleteCartListingAndRemoveLocally(listingId)),
  callSetInitialValues: (setInitialValues, values) => dispatch(setInitialValues(values)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onAddCartToUser: (cartId, email) => dispatch(addCartToUser({ cartId, email })),
  updateCartListingAmount: params => dispatch(updateCartListingAndRefetchTimeSlot(params)),
});

const CartPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl,
  withRouter
)(CartPageComponent);

CartPage.loadData = loadData;

export default CartPage;
